import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthState, AuthAction, User } from '../types/auth';
import { authService } from '../services/authService';

const initialState: AuthState = {
    user: null,
    isAuthenticated: false,
    isLoading: true, // Start with loading true to check session
    tokens: null,
    customerData: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };
        case 'LOGIN_SUCCESS':
            return {
                ...state,
                user: action.payload.user,
                tokens: action.payload.tokens,
                customerData: action.payload.customerData,
                isAuthenticated: true,
                isLoading: false,
            };
        case 'LOGOUT':
            return { ...initialState, isLoading: false };
        case 'SET_CUSTOMER_DATA':
            return { ...state, customerData: action.payload };
        default:
            return state;
    }
};

interface AuthContextType extends AuthState {
    login: (email: string, code: string) => Promise<void>;
    logout: () => void;
    validateToken: (token: string) => Promise<{ customerData: any }>;
    sendLoginCode: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    useEffect(() => {
        // Check for existing session in sessionStorage
        const checkExistingSession = () => {
            const tokens = authService.getStoredTokens();
            const user = authService.getStoredUser();

            if (tokens && user) {
                // User has valid session
                dispatch({
                    type: 'LOGIN_SUCCESS',
                    payload: {
                        user,
                        tokens,
                        customerData: null
                    }
                });
            } else {
                // No valid session found
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        };

        checkExistingSession();
    }, []);

    const sendLoginCode = async (email: string): Promise<void> => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            await authService.sendLoginCode(email);
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    const login = async (email: string, code: string): Promise<void> => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const result = await authService.verifyCode(email, code);
            dispatch({
                type: 'LOGIN_SUCCESS',
                payload: {
                    user: { email },
                    tokens: result.tokens,
                    customerData: result.customerData
                }
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    const validateToken = async (token: string): Promise<{ customerData: any }> => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const result = await authService.validateToken(token);
            console.log('validateToken result:', result);
            dispatch({
                type: 'LOGIN_SUCCESS',
                payload: {
                    user: { email: result.email },
                    tokens: result.tokens,
                    customerData: result.customerData
                }
            });
            return { customerData: result.customerData };
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    const logout = (): void => {
        authService.clearStorage();
        dispatch({ type: 'LOGOUT' });
    };

    const value: AuthContextType = {
        ...state,
        login,
        logout,
        validateToken,
        sendLoginCode,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};